import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authUtil } from 'src/utils';
import { useEffect, useRef } from 'react';

export type FeedbackDigestStatus =
  | 'pending'
  | 'aggregating'
  | 'summarizing'
  | 'synthesizing'
  | 'completed'
  | 'error';

export type ReactionMix = {
  LIKE: number;
  LOVE: number;
  LAUGH: number;
  ANGRY: number;
  SURPRISE: number;
  SAD: number;
};

export type FeedbackGlance = {
  commentCount: number;
  readerCount: number;
  finishedCount: number;
  reviewCount: number;
  avgOverallRating: number | null;
  readyToPublishCount: number;
  reactionMix: ReactionMix;
  reactionTotal: number;
  topChapters: { index: number; title: string; count: number }[];
  engagementByChapter: { index: number; title: string; count: number }[];
  slowestStretch: string | null;
};

export type FeedbackDigestReport = {
  execSummary?: string;
  priorities?: { n: number; text: string }[];
  themes?: {
    title: string;
    sentiment: 'positive' | 'negative' | 'mixed';
    readerLabel: string;
    evidence: { tag: string; text: string }[];
  }[];
  issues?: {
    rank: number;
    title: string;
    severity: 1 | 2 | 3;
    readerLabel: string;
    chapters: string;
    evidence: { tag: string; text: string }[];
  }[];
  hotspots?: {
    ch: string;
    title: string;
    count: number;
    net: 'positive' | 'negative' | 'neutral';
    note: string;
  }[];
  engagement?: { note?: string; dropOff?: string; enthusiasm?: string };
  ratings?: {
    overall: number | null;
    plot: number | null;
    grammar: number | null;
    readyToPublishCount: number;
    reviewCount: number;
  };
};

export type FeedbackDigest = {
  status: FeedbackDigestStatus;
  report?: FeedbackDigestReport;
  reportMarkdown?: string;
  model?: string;
  creditsCharged?: number;
  stats?: {
    commentCount?: number;
    readerCount?: number;
    reviewCount?: number;
    avgOverallRating?: number;
  };
  completedAt?: string;
  error?: string;
};

export type FeedbackDigestResponse = {
  feedbackDigest: FeedbackDigest | null;
  glance: FeedbackGlance;
  credits: {
    balance: number;
    resetDate?: string;
    monthlyCredits?: number;
    productInternalId?: string;
  };
};

type FeedbackDigestProps = {
  bookId?: string;
  contentId?: string;
};

const RUNNING_STATUSES: FeedbackDigestStatus[] = [
  'pending',
  'aggregating',
  'summarizing',
  'synthesizing',
];

const baseUrl = (bookId: string, contentId: string) =>
  `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/books/${bookId}/content/${contentId}/feedback-digest`;

const useFeedbackDigest = ({ bookId, contentId }: FeedbackDigestProps) => {
  const queryClient = useQueryClient();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const queryKey = ['feedbackDigest', bookId, contentId];

  const feedbackDigestQuery = useQuery<FeedbackDigestResponse>(
    queryKey,
    async () => {
      const response = await fetch(
        baseUrl(bookId as string, contentId as string),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'br-token': await authUtil.getFreshIdToken(),
          },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch feedback digest');
      }
      return response.json();
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      staleTime: 1000 * 5,
      cacheTime: 1000 * 60 * 10,
      retry: 1,
      enabled: !!bookId && !!contentId,
    }
  );

  // Poll while a run is in progress.
  useEffect(() => {
    const status = feedbackDigestQuery.data?.feedbackDigest?.status;
    const shouldPoll = !!status && RUNNING_STATUSES.includes(status);

    if (shouldPoll) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      pollingIntervalRef.current = setInterval(() => {
        feedbackDigestQuery.refetch();
      }, 4000);
    } else if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [
    feedbackDigestQuery.data?.feedbackDigest?.status,
    feedbackDigestQuery.refetch,
  ]);

  const startMutation = useMutation(
    async (body?: { reportLanguage?: string }) => {
      const response = await fetch(
        baseUrl(bookId as string, contentId as string),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'br-token': await authUtil.getFreshIdToken(),
          },
          body: JSON.stringify(body || {}),
        }
      );
      const data = await response.json().catch(() => ({}));
      return { ok: response.ok, status: response.status, data };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  return {
    feedbackDigestQuery,
    // Returns {ok, status, data}; caller handles 402/409.
    startFeedbackDigest: startMutation.mutateAsync,
    isStarting: startMutation.isLoading,
  };
};

export default useFeedbackDigest;
